import { Capacitor } from '@capacitor/core'
import { Directory, Filesystem } from '@capacitor/filesystem'

import type { ItemFull } from './lingodb'
import { isNative } from './platform'

/**
 * Persistent per-item media cache for patient-built exercises.
 *
 * The picker (`resources/js/views/workspace/patient/ExercisesPick.vue`) won't
 * let the patient enter the training screen for an exercise until every
 * referenced item has every offered medium on disk — that's the contract
 * the drill UI relies on to run purely offline. We sidestep tracking that
 * state on the server because it's fundamentally a per-device property: a
 * fresh install on a second device should re-prepare even though the
 * exercise row is already there server-side.
 *
 * The cache covers every lingodb media kind the training screen could play:
 * image1 + image2 + image_help, mouth (video), and six audio variants used
 * by the help-grid rows. The cache lib doesn't know which kinds the
 * training screen *uses* — it caches whatever the upstream item exposes,
 * the view decides how to map them onto help buttons.
 *
 * Layout under `Directory.Data` (default `exercises` namespace):
 *
 *   lingodb/items/item-<id>-<kind>.<ext>    — raw bytes, one file per kind;
 *                                            ext is mp3/mp4/webp (see KIND_EXTENSION)
 *   lingodb/exercises/exercise-<id>.json    — per-exercise manifest
 *
 * Item files are keyed by item id + kind (not exercise id), so two
 * exercises that share an item share the bytes. The manifest is per-
 * exercise to keep "exercise X is X% complete" calls cheap and to localise
 * corruption.
 *
 * **Namespaces.** Patient-authored exercises (`exercises.id`) and therapist
 * homework (`therapist_exercises.id`) are independent autoincrement id spaces
 * that collide, so the homework store passes `namespace: 'homework'` to keep
 * its manifests *and* item files in a sibling subtree
 * (`lingodb/homework/{items,exercise-<id>.json}`). The default `'exercises'`
 * namespace keeps the original paths verbatim — existing installs don't
 * re-download. Items are namespaced (not shared) so each namespace's
 * {@link pruneOrphanItems} sweep can't delete the other's bytes; the small
 * cost is that an item used in both is cached twice.
 *
 * On web every helper is a no-op or returns null — therapists run in a
 * browser without the Filesystem plugin and have no offline requirement.
 * Mirrors the rules established in `resources/js/lib/thumbnail-cache.ts`.
 */

const ITEMS_DIR = 'lingodb/items'
const MANIFESTS_DIR = 'lingodb/exercises'

/** Default cache namespace — its paths are the original ones (no migration). */
export const DEFAULT_CACHE_NAMESPACE = 'exercises'

/** Item-media directory for a namespace. Default keeps the historic path. */
function itemsDirFor(namespace: string): string {
  return namespace === DEFAULT_CACHE_NAMESPACE ? ITEMS_DIR : `lingodb/${namespace}/items`
}

/** Manifest directory for a namespace. Default keeps the historic path. */
function manifestsDirFor(namespace: string): string {
  return namespace === DEFAULT_CACHE_NAMESPACE ? MANIFESTS_DIR : `lingodb/${namespace}`
}

/**
 * The @capacitor/filesystem Android implementation ignores `recursive: true`
 * on `downloadFile` (see LegacyFilesystemImplementation.kt:103-120 — it goes
 * straight to FileOutputStream without ever mkdir'ing the parent), so every
 * media download fails with ENOENT on first launch when lingodb/items/
 * doesn't exist yet. iOS auto-creates parents either way. We mkdir once per
 * session before the first download to side-step the plugin bug. writeFile
 * already honors `recursive: true` on Android, so manifest writes are fine.
 */
const ensuredDirs = new Set<string>()
async function ensureCacheDir(path: string): Promise<void> {
  if (!isNative || ensuredDirs.has(path)) return
  try {
    await Filesystem.mkdir({ path, directory: Directory.Data, recursive: true })
  } catch {
    // Already exists — fine. The plugin rejects on exists even with recursive.
  }
  ensuredDirs.add(path)
}

/**
 * The file extension a cached item file must end with for each kind.
 *
 * Capacitor's URL scheme handlers map MIME type from the file extension
 * — `.bin` falls back to `application/octet-stream`, which iOS WKWebView
 * refuses to load through `<audio>` (`WebViewAssetHandler.isMediaExtension`
 * also gates the mmap'd urlResponse path on a media extension). Android's
 * `WebViewLocalServer.getMimeType` likewise returns null for `.bin` and
 * the stream sniffer doesn't recognise MP3. So unless we name the cached
 * file with a real audio/video extension, the help-row audio buttons
 * silently produce no sound (the `<audio>` element fires `error` with
 * `MEDIA_ERR_SRC_NOT_SUPPORTED`).
 *
 * Images are forgiving — WebKit's image decoder magic-byte-sniffs every
 * common container regardless of MIME — but we still apply a proper
 * extension here for consistency and so prune sweeps remain
 * extension-agnostic.
 *
 * Lingodb's S3 keys are .mp3 for every audio kind, .mp4 for mouth, and
 * .webp for images; we hardcode rather than parsing the URL because the
 * presigned host portion can carry query params that confuse naive
 * extension parsing, and the upstream format set is small and stable.
 */
const KIND_EXTENSION: Record<string, string> = {
  image1: 'webp',
  image2: 'webp',
  image_help: 'webp',
  mouth: 'mp4',
  audio_full: 'mp3',
  audio_start: 'mp3',
  audio_first_part: 'mp3',
  audio_description: 'mp3',
  audio_sentence: 'mp3',
  audio_properties: 'mp3',
}

/**
 * Cached base URI for `Directory.Data` — resolved lazily on the first
 * `resolveToCapacitorUri` call and reused thereafter. Stable within an
 * app run (only changes on app reinstall, which is also when we'd want
 * a fresh resolution anyway).
 */
let dataBaseUri: string | null = null

/**
 * Convert a relative path under `Directory.Data` to a live
 * `capacitor://` URI usable as an `<img src>` / `<audio src>`. Resolves
 * the current iOS App Container UUID at runtime so reinstalls don't
 * invalidate the URI (which is what happens when you persist a
 * pre-resolved `capacitor://` URI — the embedded UUID rotates and the
 * stored URI points at a dead container).
 */
export async function resolveToCapacitorUri(relativePath: string): Promise<string> {
  if (!isNative) return relativePath
  if (dataBaseUri === null) {
    const { uri } = await Filesystem.getUri({ path: '', directory: Directory.Data })
    dataBaseUri = uri.endsWith('/') ? uri.slice(0, -1) : uri
  }
  return Capacitor.convertFileSrc(`${dataBaseUri}/${relativePath}`)
}

/**
 * Every media kind the training screen could ever need. Adding a kind here
 * automatically expands what the picker prefetches and what the manifest
 * tracks. Ordering is just for readability — `complete` computes the same
 * way regardless.
 *
 * Keys match `ItemFullMedia` (see `resources/js/lib/lingodb.ts:90-101`).
 */
export const MEDIA_KINDS = [
  'image1',
  'image2',
  'image_help',
  'mouth',
  'audio_full',
  'audio_start',
  'audio_first_part',
  'audio_description',
  'audio_sentence',
  'audio_properties',
] as const
export type MediaKind = (typeof MEDIA_KINDS)[number]

/**
 * Lightweight subset of `ItemFull` (`lib/lingodb.ts`) persisted alongside
 * the media. Holds everything the training screen needs to run the drill
 * offline — particularly `content`, the German word the patient should
 * say. Caching it means the training view doesn't need to make one
 * `fetchItem` round-trip per item at boot (was costing multiple seconds
 * on a 10-item exercise).
 */
export interface CachedItemMeta {
  id: number
  content: string
  ipa: string | null
}

/**
 * The cached state of one item's media. Every kind has its own
 * `<kind>_local_uri: string | null` field — null means either the upstream
 * item doesn't offer this medium, or we haven't downloaded it (yet, or it
 * keeps failing). `complete` summarises: true iff every present upstream
 * URL has a corresponding local file. Cheap to evaluate at cold-launch
 * from the manifest alone.
 *
 * `item` holds the lightweight item metadata so the training screen
 * boots entirely from disk — see {@link CachedItemMeta}.
 */
export type CachedItemMedia = {
  [K in MediaKind as `${K}_local_uri`]: string | null
} & {
  item: CachedItemMeta | null
  complete: boolean
  /** Wall-clock timestamp of the last successful write of any kind. */
  cached_at: string | null
}

export type ExerciseManifest = Record<number, CachedItemMedia>

export function emptyCachedItemMedia(): CachedItemMedia {
  const partial: Record<string, unknown> = {}
  for (const k of MEDIA_KINDS) {
    partial[`${k}_local_uri`] = null
  }
  partial.item = null
  partial.complete = false
  partial.cached_at = null
  return partial as CachedItemMedia
}

// --- Manifest index per exercise -----------------------------------------

/**
 * Read the on-disk manifest for one exercise. Returns {} on web, on first
 * call, or when the file is missing/corrupt — never throws. Mirrors the
 * `thumbnail-cache.ts` `loadCachedTopics` contract.
 *
 * Every `_local_uri` in the returned manifest is a RELATIVE path under
 * `Directory.Data` — NOT a renderable URI. The training view (the only
 * consumer that renders media) calls {@link materializeEntry} at boot
 * to resolve those paths into live `capacitor://` URIs. Keeping the
 * stored form path-based means a reinstall (which rotates the iOS App
 * Container UUID) doesn't invalidate everything.
 *
 * Stale-entry handling: anything failing {@link hasCurrentShape} is
 * dropped on load. That includes pre-relative-path entries whose
 * `_local_uri` is a baked `capacitor://...UUID...` string — those would
 * have pointed at a Container that no longer exists.
 */
export async function loadExerciseManifest(
  exerciseId: number,
  namespace: string = DEFAULT_CACHE_NAMESPACE,
): Promise<ExerciseManifest> {
  if (!isNative) return {}
  try {
    const { data } = await Filesystem.readFile({
      path: manifestPath(exerciseId, namespace),
      directory: Directory.Data,
      encoding: 'utf8' as never,
    })
    const text = typeof data === 'string' ? data : await (data as Blob).text()
    const parsed = JSON.parse(text)
    if (parsed === null || typeof parsed !== 'object') return {}

    const out: ExerciseManifest = {}
    for (const [itemId, entry] of Object.entries(parsed as Record<string, unknown>)) {
      const idNum = Number.parseInt(itemId, 10)
      if (Number.isNaN(idNum)) continue
      if (!hasCurrentShape(entry)) continue
      out[idNum] = entry as CachedItemMedia
    }
    return out
  } catch {
    return {}
  }
}

/**
 * Convert an entry's stored relative-path `_local_uri` fields into live
 * `capacitor://` URIs the WebView can render. The result is suitable
 * for use as `<img src>` / `<audio src>` in the current session.
 *
 * Pure on the input — returns a new object. Never persist the result
 * (the embedded Container UUID would go stale on the next reinstall).
 *
 * On web every kind is already a presigned remote URL (see the
 * `!isNative` branch in {@link downloadItemMedia}), so this is a no-op
 * shallow clone.
 */
export async function materializeEntry(entry: CachedItemMedia): Promise<CachedItemMedia> {
  if (!isNative) return { ...entry }
  const out: CachedItemMedia = { ...entry }
  for (const k of MEDIA_KINDS) {
    const path = entry[`${k}_local_uri`]
    if (typeof path === 'string' && path.length > 0) {
      out[`${k}_local_uri`] = await resolveToCapacitorUri(path)
    }
  }
  return out
}

/**
 * True iff `entry` matches the current persisted shape: a
 * `CachedItemMedia` whose `_local_uri` fields hold either null or a
 * relative path. Used by {@link loadExerciseManifest} to drop
 * pre-migration entries.
 *
 * Sniffs out the pre-relative-path format too — any value starting
 * with `capacitor://` is an absolute URI baked at write time, which
 * goes stale across app reinstalls (the iOS Container UUID rotates).
 */
function hasCurrentShape(entry: unknown): boolean {
  if (entry === null || typeof entry !== 'object') return false
  const obj = entry as Record<string, unknown>
  for (const k of MEDIA_KINDS) {
    if (!(`${k}_local_uri` in obj)) return false
    const value = obj[`${k}_local_uri`]
    if (value !== null && typeof value !== 'string') return false
    if (typeof value === 'string' && value.startsWith('capacitor://')) return false
    // Reject legacy `.bin` paths — those files have an extension that
    // resolves to `application/octet-stream`, which iOS WKWebView won't
    // accept through `<audio>`. Forcing the entry to fail this shape
    // check makes the picker re-download into a file with a proper
    // extension via `itemFilePath`.
    if (typeof value === 'string' && value.endsWith('.bin')) return false
  }
  // `item` was added when we moved per-item metadata onto the manifest
  // (training-view boot speed-up). Any entry without it is stale — drop
  // it so the picker re-prepares and the new entry includes `content`.
  const item = obj.item
  if (item === null || typeof item !== 'object') return false
  const itemObj = item as Record<string, unknown>
  if (typeof itemObj.id !== 'number') return false
  if (typeof itemObj.content !== 'string') return false
  return 'complete' in obj && 'cached_at' in obj
}

export async function saveExerciseManifest(
  exerciseId: number,
  manifest: ExerciseManifest,
  namespace: string = DEFAULT_CACHE_NAMESPACE,
): Promise<void> {
  if (!isNative) return
  await Filesystem.writeFile({
    path: manifestPath(exerciseId, namespace),
    data: JSON.stringify(manifest),
    directory: Directory.Data,
    encoding: 'utf8' as never,
    recursive: true,
  })
}

/**
 * Delete the manifest file for one exercise. Tolerates a missing file
 * (e.g. exercise was created and removed before any download ran).
 *
 * Does NOT touch the per-item media files — those may still be referenced
 * by other exercises. {@see pruneOrphanItems} sweeps unreferenced files.
 */
export async function pruneExerciseManifest(
  exerciseId: number,
  namespace: string = DEFAULT_CACHE_NAMESPACE,
): Promise<void> {
  if (!isNative) return
  try {
    await Filesystem.deleteFile({
      path: manifestPath(exerciseId, namespace),
      directory: Directory.Data,
    })
  } catch {
    // File didn't exist or was already gone — harmless.
  }
}

// --- Item media downloads -------------------------------------------------

/**
 * Download whatever of an item's media kinds isn't yet on disk. Tolerant
 * of any kind being null upstream (skipped) and of per-file network
 * failure (partial result returned). Never throws to callers.
 *
 * Uses `Filesystem.downloadFile()` rather than `fetch()` + `writeFile()`
 * so the native HTTP stack handles the download — same reason as
 * `thumbnail-cache.ts:79-86` (Hetzner S3 doesn't allowlist `capacitor://
 * localhost` so the WebView's `fetch` preflight fails).
 *
 * `complete` is recomputed from scratch: true iff every kind the upstream
 * item offers has a non-null local URI. This means a manifest entry from
 * the v1 cache (only image + audio fields populated) automatically flips
 * to `complete: false` and the missing kinds get pulled on the next refresh.
 */
export async function downloadItemMedia(
  itemFull: ItemFull,
  previous?: CachedItemMedia,
  namespace: string = DEFAULT_CACHE_NAMESPACE,
): Promise<CachedItemMedia> {
  const itemId = itemFull.id
  const media = itemFull.media
  const meta: CachedItemMeta = {
    id: itemFull.id,
    content: itemFull.content,
    ipa: itemFull.ipa,
  }

  if (!isNative) {
    // On web there is no cache — caller renders the presigned URLs directly
    // and we report "complete" to keep the picker out of the preparing state.
    const result = emptyCachedItemMedia()
    result.item = meta
    for (const kind of MEDIA_KINDS) {
      result[`${kind}_local_uri`] = media[kind]?.url ?? null
    }
    result.complete = true
    result.cached_at = new Date().toISOString()
    return result
  }

  const result = emptyCachedItemMedia()
  result.item = meta
  let progressed = false
  let allOk = true

  for (const kind of MEDIA_KINDS) {
    const url = media[kind]?.url ?? null
    const priorPath = previous?.[`${kind}_local_uri`] ?? null

    if (url === null) {
      // Upstream doesn't offer this medium. Drop any stale local pointer
      // and don't count it against completeness.
      result[`${kind}_local_uri`] = null
      continue
    }
    if (priorPath !== null) {
      // Already on disk from a previous run — reuse the same relative
      // path. (Note this won't be a `capacitor://` URI; loadExerciseManifest
      // drops those at parse time. So `priorPath` is always a relative
      // path under Directory.Data here.)
      result[`${kind}_local_uri`] = priorPath
      continue
    }
    // Needs a fresh download. We store the RELATIVE path — consumers
    // (training view) resolve it to a live capacitor:// URI at render
    // time via {@link materializeEntry}. Keeping the path on disk means
    // the next install with a different App Container UUID still
    // finds the file.
    const path = itemFilePath(itemId, kind, namespace)
    try {
      await ensureCacheDir(itemsDirFor(namespace))
      await Filesystem.downloadFile({
        url,
        path,
        directory: Directory.Data,
        recursive: true,
      })
      result[`${kind}_local_uri`] = path
      progressed = true
    } catch (e) {
      console.warn(`media download failed for item ${itemId}, kind ${kind}:`, e)
      allOk = false
    }
  }

  result.complete = allOk
  result.cached_at = progressed ? new Date().toISOString() : (previous?.cached_at ?? null)
  return result
}

/**
 * True iff the cached entry was marked complete by a previous successful
 * `downloadItemMedia` call. Cheap to evaluate at cold-launch from the
 * persisted manifest alone.
 */
export function isItemComplete(m: CachedItemMedia | undefined): boolean {
  return m?.complete === true
}

// --- Cross-exercise orphan sweep -----------------------------------------

/**
 * Delete any item file in `lingodb/items/` whose id no longer appears in
 * `keep`. Called by `useExercisesStore` after a refresh / delete so the
 * cache reclaims space for items no surviving exercise references.
 *
 * Tolerates a missing directory (first launch, post-reset).
 */
export async function pruneOrphanItems(
  keepItemIds: number[],
  namespace: string = DEFAULT_CACHE_NAMESPACE,
): Promise<void> {
  if (!isNative) return
  const itemsDir = itemsDirFor(namespace)
  const keepSet = new Set(keepItemIds.map(String))
  try {
    const { files } = await Filesystem.readdir({ path: itemsDir, directory: Directory.Data })
    for (const entry of files) {
      // Prefix-only — matches both the legacy `.bin` files (deprecated
      // since we adopted real per-kind extensions for MIME-type reasons,
      // see `KIND_EXTENSION` at the top of this file) and the current
      // `.mp3` / `.mp4` / `.webp` files. The legacy `.bin` orphans of a
      // surviving item get reclaimed the next time the item is removed
      // from every exercise — not aggressively, to avoid racing with the
      // re-download that hasCurrentShape's invalidation triggers.
      const match = /^item-(\d+)-/.exec(entry.name)
      if (!match) continue
      if (!keepSet.has(match[1])) {
        await Filesystem.deleteFile({
          path: `${itemsDir}/${entry.name}`,
          directory: Directory.Data,
        }).catch(() => {
          // Race with another writer is harmless — fall through.
        })
      }
    }
  } catch {
    // Directory doesn't exist yet → nothing to prune.
  }
}

// --- internals -----------------------------------------------------------

function itemFilePath(itemId: number, kind: MediaKind, namespace: string): string {
  // Extension drives MIME type when the file is served via capacitor:// —
  // see `KIND_EXTENSION` above for why this matters for audio/video on
  // iOS WKWebView and Android's WebViewLocalServer.
  const ext = KIND_EXTENSION[kind] ?? 'bin'
  return `${itemsDirFor(namespace)}/item-${itemId}-${kind}.${ext}`
}

function manifestPath(exerciseId: number, namespace: string): string {
  return `${manifestsDirFor(namespace)}/exercise-${exerciseId}.json`
}
